import React from 'react';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const MainNavbar = () => {
  const { user, logout, isDualRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper to check if a path is active
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const activeLinkStyle = {
    fontWeight: '600',
    borderBottom: '2px solid rgba(255,255,255,0.8)',
  };

  // Determine the home route based on user role
  const getHomeRoute = () => {
    if (!user) return '/';
    
    // Committee/Admin roles (including SDA) go to admin dashboard
    if ([
      'admin',
      'uisp_territorial',
      'uisp_regional',
      'uisp_national',
      'uisp_territorial_secretary',
      'uisp_regional_secretary',
      'uisp_nazionale_secretary',
      'sda_territorial',
      'sda_regional',
      'sda_nazionale',
      'cordinator_territorial',
      'cordinator_regional',
      'cordinator_nazionale'
    ].includes(user.role)) {
      return '/admin';
    }

    // Association role goes to association dashboard
    if (user.role === 'association') {
      return '/association';
    }

    // Default to landing page
    return '/';
  };

  // Check if user is a committee user (can manage events)
  const isCommitteeRole = user && [
    'admin',
    'uisp_territorial',
    'uisp_regional',
    'uisp_national',
    'uisp_territorial_secretary',
    'uisp_regional_secretary',
    'uisp_nazionale_secretary',
    'sda_territorial',
    'sda_regional',
    'sda_nazionale',
    'cordinator_territorial',
    'cordinator_regional',
    'cordinator_nazionale'
  ].includes(user.role);

  // Check if user is SDA (has access to event planning tools)
  const isSdaRole = user && [
    'admin',
    'sda_territorial',
    'sda_regional',
    'sda_nazionale'
  ].includes(user.role);

  // Check if user has association access (can register members)
  const hasAssociationAccess = user && (user.role === 'association' || (isDualRole && isDualRole()));

  if (!user) return null; // Don't render navbar if not logged in
  return (
    <Navbar style={{ backgroundColor: "#006e39" }} variant="dark" expand="lg" className="mb-4">
      <Container>
        <Navbar.Brand 
          as={Link} 
          to={getHomeRoute()}
          style={{ cursor: 'pointer' }}
        >
          UISP Iscrizioni
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            {/* Admin & Committee Navigation */}
            {isCommitteeRole && (
              <>
                <Nav.Link as={Link} to="/admin" style={location.pathname === '/admin' ? activeLinkStyle : {}}>
                  <i className="bi bi-speedometer2 me-1"></i>
                  Dashboard Admin
                </Nav.Link>
                <Nav.Link as={Link} to="/admin/events/list" style={isActive('/admin/events/list') ? activeLinkStyle : {}}>
                  <i className="bi bi-calendar-check me-1"></i>
                  Eventi
                </Nav.Link>
                <Nav.Link as={Link} to="/admin/events/create" style={isActive('/admin/events/create') ? activeLinkStyle : {}}>
                  <i className="bi bi-plus-circle me-1"></i>
                  Crea Evento
                </Nav.Link>
              </>
            )}

            {/* SDA Event Planning Tools */}
            {isSdaRole && (
              <>
                <Nav.Link as={Link} to="/admin/sda-dashboard" style={isActive('/admin/sda-dashboard') ? activeLinkStyle : {}}>
                  <i className="bi bi-graph-up me-1"></i>
                  Dashboard SDA
                </Nav.Link>
                <Nav.Link as={Link} to="/admin/sda-event-planning" style={isActive('/admin/sda-event-planning') ? activeLinkStyle : {}}>
                  <i className="bi bi-calendar-event me-1"></i>
                  Pianifica Eventi
                </Nav.Link>
              </>
            )}

            {/* Association Navigation - shown for both association role and dual-role committee users */}
            {hasAssociationAccess && (
              <>
                <Nav.Link as={Link} to="/association" style={location.pathname === '/association' ? activeLinkStyle : {}}>
                  <i className="bi bi-building me-1"></i>
                  Dashboard Associazione
                </Nav.Link>
                <Nav.Link as={Link} to="/association/members" style={isActive('/association/members') ? activeLinkStyle : {}}>
                  <i className="bi bi-people me-1"></i>
                  Soci
                </Nav.Link>
              </>
            )}
          </Nav>

          <Nav>
            <NavDropdown 
              title={
                <span>
                  <i className="bi bi-person-circle me-2"></i>
                  {user.username || user.nome || user.email || 'User'}
                </span>
              } 
              id="user-dropdown" 
              align="end"
            >
              <NavDropdown.Item disabled>
                <strong>Ruolo:</strong> {user.role}
              </NavDropdown.Item>
              {user.discipline && (
                <NavDropdown.Item disabled>
                  <strong>Disciplina:</strong> {user.discipline}
                </NavDropdown.Item>
              )}
              {user.association_id && (
                <NavDropdown.Item disabled>
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Associazione collegata
                </NavDropdown.Item>
              )}
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to="/change-password">
                <i className="bi bi-shield-lock me-2"></i>
                Modifica Password
              </NavDropdown.Item>
              <NavDropdown.Item onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default MainNavbar;
